import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Shield, ChevronRight, Copy, RotateCcw, Plus, Save, Users, DollarSign, FolderKanban } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";

// Flattened permissions to avoid Babel stack overflow
const PERM_PROGRAMS_VIEW = { key: "can_view_all_programs", label: "View all programs", help: "See every program" };
const PERM_PROGRAMS_EDIT = { key: "can_edit_programs", label: "Edit programs", help: "Modify program details" };
const PERM_PROGRAMS_ASSIGN = { key: "can_assign_resources", label: "Assign resources", help: "Add artists to programs" };
const PERM_FIN_VIEW = { key: "can_view_financials", label: "View financials", help: "See values and margins" };
const PERM_FIN_COSTING = { key: "can_view_costing", label: "View costing module", help: "Access Project Costing" };
const PERM_FIN_RECEIVABLES = { key: "can_manage_receivables", label: "Manage receivables", help: "Handle invoices" };
const PERM_TEAM_MANAGE = { key: "can_manage_team", label: "Manage team", help: "Add and remove members" };
const PERM_TEAM_LEAVES = { key: "can_approve_leaves", label: "Approve leaves", help: "Approve PTO requests" };
const PERM_TEAM_CLIENTS = { key: "can_manage_clients", label: "Manage clients", help: "Edit client info" };
const PERM_EXEC_CMD = { key: "can_access_command_center", label: "Command Center", help: "Executive dashboard" };

const ROLE_COLORS = {
  "COO": "bg-amber-500", "Admin": "bg-purple-500", "Program Manager": "bg-blue-500",
  "Marketing PM": "bg-pink-500", "Production": "bg-emerald-500", "Sales": "bg-orange-500",
  "Artist": "bg-cyan-500", "Accountant": "bg-indigo-500", "VEXOS Executive": "bg-slate-500",
  "Sales Assistant": "bg-teal-500"
};

function PermRow({ perm, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg hover:bg-muted/50">
      <div>
        <p className="text-sm font-medium">{perm.label}</p>
        <p className="text-xs text-muted-foreground">{perm.help}</p>
      </div>
      <Switch checked={value === true} onCheckedChange={onChange} />
    </div>
  );
}

function GroupHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4 text-muted-foreground" />
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{label}</h3>
    </div>
  );
}

export default function RolesPermissions() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showNewRole, setShowNewRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [duplicateFrom, setDuplicateFrom] = useState(null);

  useEffect(() => { fetchRoles(); }, []);

  async function fetchRoles() {
    try {
      const res = await apiClient.get("/roles");
      const data = res.data.roles || [];
      setRoles(data);
      if (data.length > 0 && !selectedRole) {
        setSelectedRole(data[0].name);
        setPermissions(data[0].permissions || {});
      }
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }

  function selectRole(name) {
    if (hasChanges && !confirm("Discard changes?")) return;
    setSelectedRole(name);
    const r = roles.find(x => x.name === name);
    setPermissions(r?.permissions || {});
    setHasChanges(false);
  }

  function toggle(key) {
    setPermissions(p => ({ ...p, [key]: !p[key] }));
    setHasChanges(true);
  }

  async function save() {
    setSaving(true);
    try {
      await apiClient.put("/roles/" + selectedRole + "/permissions", { permissions });
      toast.success("Saved");
      setHasChanges(false);
      fetchRoles();
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  }

  function reset() {
    const r = roles.find(x => x.name === selectedRole);
    setPermissions(r?.permissions || {});
    setHasChanges(false);
  }

  function startDuplicate(name) {
    setDuplicateFrom(name);
    setNewRoleName(name + " (Copy)");
    setShowNewRole(true);
  }

  async function createRole() {
    if (!newRoleName.trim()) { toast.error("Name required"); return; }
    try {
      const base = duplicateFrom ? roles.find(x => x.name === duplicateFrom)?.permissions : {};
      await apiClient.post("/roles", { name: newRoleName, permissions: base });
      toast.success("Created");
      setShowNewRole(false);
      setNewRoleName("");
      setDuplicateFrom(null);
      fetchRoles();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  const color = ROLE_COLORS[selectedRole] || "bg-gray-500";

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-light tracking-tight">Roles & Permissions</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure access levels</p>
      </div>

      <div className="flex gap-6">
        <Card className="w-72 shrink-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Roles</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => { setDuplicateFrom(null); setNewRoleName(""); setShowNewRole(true); }}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            <ScrollArea className="h-[500px]">
              <div className="space-y-1">
                {roles.map(r => {
                  const c = ROLE_COLORS[r.name] || "bg-gray-500";
                  const sel = selectedRole === r.name;
                  return (
                    <button key={r.name} onClick={() => selectRole(r.name)}
                      className={"w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors " + (sel ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>
                      <div className={"w-2 h-2 rounded-full " + c} />
                      <span className="flex-1 text-sm font-medium">{r.name}</span>
                      {sel && <ChevronRight className="w-4 h-4" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <Card className="flex-1">
          {selectedRole ? (
            <>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={"w-3 h-3 rounded-full " + color} />
                    <div>
                      <CardTitle>{selectedRole}</CardTitle>
                      <CardDescription>Configure permissions</CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => startDuplicate(selectedRole)}>
                      <Copy className="w-4 h-4 mr-1" /> Duplicate
                    </Button>
                    {hasChanges && (
                      <>
                        <Button size="sm" variant="ghost" onClick={reset}><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
                        <Button size="sm" onClick={save} disabled={saving}><Save className="w-4 h-4 mr-1" /> Save</Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="p-6 space-y-8">
                    <div>
                      <GroupHeader icon={FolderKanban} label="Programs" />
                      <PermRow perm={PERM_PROGRAMS_VIEW} value={permissions.can_view_all_programs} onChange={() => toggle("can_view_all_programs")} />
                      <PermRow perm={PERM_PROGRAMS_EDIT} value={permissions.can_edit_programs} onChange={() => toggle("can_edit_programs")} />
                      <PermRow perm={PERM_PROGRAMS_ASSIGN} value={permissions.can_assign_resources} onChange={() => toggle("can_assign_resources")} />
                      <Separator className="mt-6" />
                    </div>
                    <div>
                      <GroupHeader icon={DollarSign} label="Financials & Costing" />
                      <PermRow perm={PERM_FIN_VIEW} value={permissions.can_view_financials} onChange={() => toggle("can_view_financials")} />
                      <PermRow perm={PERM_FIN_COSTING} value={permissions.can_view_costing} onChange={() => toggle("can_view_costing")} />
                      <PermRow perm={PERM_FIN_RECEIVABLES} value={permissions.can_manage_receivables} onChange={() => toggle("can_manage_receivables")} />
                      <Separator className="mt-6" />
                    </div>
                    <div>
                      <GroupHeader icon={Users} label="Team & Resources" />
                      <PermRow perm={PERM_TEAM_MANAGE} value={permissions.can_manage_team} onChange={() => toggle("can_manage_team")} />
                      <PermRow perm={PERM_TEAM_LEAVES} value={permissions.can_approve_leaves} onChange={() => toggle("can_approve_leaves")} />
                      <PermRow perm={PERM_TEAM_CLIENTS} value={permissions.can_manage_clients} onChange={() => toggle("can_manage_clients")} />
                      <Separator className="mt-6" />
                    </div>
                    <div>
                      <GroupHeader icon={Shield} label="Executive" />
                      <PermRow perm={PERM_EXEC_CMD} value={permissions.can_access_command_center} onChange={() => toggle("can_access_command_center")} />
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </>
          ) : (
            <CardContent className="h-[560px] flex items-center justify-center">
              <p className="text-muted-foreground">Select a role</p>
            </CardContent>
          )}
        </Card>
      </div>

      <Dialog open={showNewRole} onOpenChange={setShowNewRole}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{duplicateFrom ? "Duplicate Role" : "Create Role"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Role Name</label>
            <Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="e.g., Account Manager" className="mt-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRole(false)}>Cancel</Button>
            <Button onClick={createRole}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
